import { useMemo, useState } from 'react';
import { requireSatellite } from './config';
import { SatelliteLayout } from './layout';
import { trackSatelliteAction } from './analytics';
import {
  ROTATION_PRESETS,
  addDays,
  addMonths,
  daysInMonth,
  formatYmd,
  monthLabel,
  nextExchangeWeekday,
  parentOnDate,
  parseYmd,
  startOfMonth,
  todayYmd,
  weekdayMonday0,
  weekdayName,
  WEEKDAY_OPTIONS,
  type ParentId,
  type Ymd,
} from './dates';
import { buildParentCalendar, downloadIcs } from './ics';
import { MonthGrid } from './MonthGrid';

const CONFIG = requireSatellite('weekonweekoff.com');
const CYCLE = ROTATION_PRESETS['week-on-week-off']!.cycle;

export function WeekOnWeekOffPage() {
  const [parentA, setParentA] = useState('Parent A');
  const [parentB, setParentB] = useState('Parent B');
  const [startInput, setStartInput] = useState(formatYmd(todayYmd()));
  const [weekday, setWeekday] = useState(4);
  const [exchangeTime, setExchangeTime] = useState('18:00');

  const start = parseYmd(startInput) ?? todayYmd();
  const anchor = nextExchangeWeekday(start, weekday);

  const months = useMemo(() => {
    const first = startOfMonth(anchor);
    return [first, addMonths(first, 1), addMonths(first, 2)];
  }, [anchor]);

  const names = { A: parentA.trim() || 'Parent A', B: parentB.trim() || 'Parent B' };

  function exportCalendar() {
    const days = [];
    for (let i = 0; i < 365; i += 1) {
      const date = addDays(anchor, i);
      days.push({ date, parent: parentOnDate(date, anchor, CYCLE) });
    }
    downloadIcs(
      'week-on-week-off.ics',
      buildParentCalendar(days, names, 'Week on week off'),
    );
    trackSatelliteAction('Satellite Calendar Exported', CONFIG, { rotation: 'week-on-week-off' });
  }

  return (
    <SatelliteLayout config={CONFIG}>
      <form className='grid gap-4 sm:grid-cols-2 print:hidden'>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>First parent</span>
          <input
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={parentA}
            onChange={(event) => setParentA(event.target.value)}
          />
        </label>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>Second parent</span>
          <input
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={parentB}
            onChange={(event) => setParentB(event.target.value)}
          />
        </label>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>Start looking from</span>
          <input
            type='date'
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={startInput}
            onChange={(event) => setStartInput(event.target.value)}
          />
        </label>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>Exchange weekday</span>
          <select
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={weekday}
            onChange={(event) => setWeekday(Number(event.target.value))}
          >
            {WEEKDAY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className='text-sm sm:col-span-2'>
          <span className='mb-1 block font-medium'>Exchange time (for your notes)</span>
          <input
            type='time'
            className='w-full rounded-lg border border-gray-300 px-3 py-2 sm:w-48'
            value={exchangeTime}
            onChange={(event) => setExchangeTime(event.target.value)}
          />
        </label>
      </form>

      <p className='mt-4 text-sm text-gray-600'>
        {names.A} starts on {weekdayName(anchor)}, {formatYmd(anchor)}. Exchanges at{' '}
        {exchangeTime}.
      </p>

      <div className='mt-6 space-y-8'>
        {months.map((month) => (
          <MonthCalendar
            key={`${month.year}-${month.month}`}
            month={month}
            anchor={anchor}
            names={names}
          />
        ))}
      </div>

      <div className='mt-6 flex flex-wrap gap-3 print:hidden'>
        <button
          type='button'
          className='rounded-xl bg-primary px-4 py-2 font-semibold text-white hover:bg-primary-hover'
          onClick={exportCalendar}
        >
          Download .ics
        </button>
        <button
          type='button'
          className='rounded-xl border border-gray-300 px-4 py-2 font-semibold hover:bg-gray-50'
          onClick={() => window.print()}
        >
          Print
        </button>
      </div>
    </SatelliteLayout>
  );
}

function MonthCalendar({
  month,
  anchor,
  names,
}: {
  month: Ymd;
  anchor: Ymd;
  names: { A: string; B: string };
}) {
  const days: { date: Ymd; parent: ParentId }[] = [];
  const count = daysInMonth(month.year, month.month);
  for (let day = 1; day <= count; day += 1) {
    const date = { year: month.year, month: month.month, day };
    days.push({ date, parent: parentOnDate(date, anchor, CYCLE) });
  }
  const pad = weekdayMonday0(month);
  return (
    <section>
      <h2 className='mb-3 text-lg font-semibold'>{monthLabel(month)}</h2>
      <MonthGrid days={days} padStart={pad} names={names} />
    </section>
  );
}
